import { graphql, useStaticQuery } from 'gatsby';
import type { FormEvent, RefObject } from 'react';
import { use, useMemo } from 'react';

import { getClubsForClubFinder } from 'src/content/club-look-ups-by-id';
import { SiteContext } from 'src/context/site-context';
import { BuildType, getClubDisplayValues } from 'src/enums/build/build-type';
import type { ClubInformation } from 'src/enums/club-information';
import { amplitudeBrandName, amplitudeTrackEvent } from 'src/helpers/amplitude-event-track';
import { navigateToGlobalPage } from 'src/helpers/navigation-helper';

export const useClubSearchForm = (
  inputRef: RefObject<HTMLInputElement | null>,
  isNavigation?: boolean,
  onSubmit?: () => void
): ((event: FormEvent<HTMLFormElement>) => void) => {
  const { buildType, language, pageLevel } = use(SiteContext);

  return (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    onSubmit?.();

    void amplitudeTrackEvent(
      `"Find a club" - ${isNavigation ? 'Navigation' : 'On the page'} - Submit click`,
      {
        custom_page: window.location.origin,
        custom_level: pageLevel,
        component: 'use club search form',
        brand: amplitudeBrandName(buildType),
        buildType,
        language,
        ctaName: 'Submit',
        formName: 'Club Search Form',
      }
    );

    const searchValue = inputRef.current?.value;
    navigateToGlobalPage('clubs', language, buildType, {
      search_location: searchValue,
    });
  };
};

const query = graphql`
  query ClubFinderSiteIds {
    clubList: file(relativePath: { eq: "cross-club/club/clubs-in-club-finder.json" }) {
      childClubJson {
        siteIds
      }
    }
  }
`;

export const useClubSearchClubs = (): ClubInformation[] => {
  const { buildType } = use(SiteContext);
  const cms = useStaticQuery<Queries.ClubFinderSiteIdsQuery>(query);
  const siteIdsForClubFinder = cms.clubList!.childClubJson!.siteIds!;

  return useMemo(
    () =>
      getClubsForClubFinder(buildType, siteIdsForClubFinder).map((club) => ({
        ...club,
        clubName:
          club.buildType === BuildType.DAVID_LLOYD_UK
            ? club.clubName
            : `${getClubDisplayValues(club.buildType).clubNamePrefix} ${club.clubName}`,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO why are the dependencies not exhaustive?
    [buildType]
  );
};

export const filterClubsBySearchKey = (searchKey: string, allClubs: ClubInformation[]) => {
  const trimmedSearchKey = searchKey?.trim() ?? '';

  if (trimmedSearchKey.length === 0) {
    return [];
  }

  return allClubs
    .map(
      (club) => [club, club.clubName.toLowerCase().indexOf(trimmedSearchKey.toLowerCase())] as const
    )
    .filter(([, index]) => index !== -1)
    .sort((a, b) => a[0].clubName.localeCompare(b[0].clubName))
    .sort((a, b) => a[1] - b[1])
    .map(([club]) => club);
};

export const useClubSearch = (searchKey: string) => {
  const allClubs = useClubSearchClubs();

  return useMemo(() => filterClubsBySearchKey(searchKey, allClubs), [searchKey, allClubs]);
};
